import React from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ToggleRow from "../../../common/ToggleRow";
import AgentConfigSection from "../AgentConfigSection";
import PublicAccessPolicyEditor from "../../../access/PublicAccessPolicyEditor";
import config from "../../../../config";
import {
  EXTERNAL_CHAT_ACCESS_MODE_DISABLED,
  EXTERNAL_CHAT_ACCESS_MODE_OPTIONS,
  EXTERNAL_CHAT_MAX_SESSIONS_PER_IDENTITY_PRESETS,
  EXTERNAL_CHAT_MAX_SESSIONS_TOTAL_PRESETS,
  EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_EXCEEDED_MESSAGE,
  EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS,
  buildExternalAccessPolicy,
  buildNumericLimitSelectOptions,
  getLimitOptionLabel,
} from "../externalAccess";

export default function AccessTab(props) {
  const {
    externalAccessPolicy,
    setExternalAccessPolicy,
    externalChatMaxSessionsPerIdentity,
    setExternalChatMaxSessionsPerIdentity,
    externalChatMaxSessionsTotal,
    setExternalChatMaxSessionsTotal,
    externalChatRateLimitMessages,
    setExternalChatRateLimitMessages,
    externalChatRateLimitPeriodValue,
    setExternalChatRateLimitPeriodValue,
    externalChatRateLimitPeriodUnit,
    setExternalChatRateLimitPeriodUnit,
    externalChatRateLimitExceededMessage,
    setExternalChatRateLimitExceededMessage,
    reviewTaskEligibleUsers,
    loadingReviewTaskEligibleUsers,
    publicShareLink,
    publicShareLinkError,
    creatingPublicShareLink,
    revokingPublicShareLink,
    publicShareLinkExpiresAt,
    setPublicShareLinkExpiresAt,
    enablePublicShareLinkExpiry,
    setEnablePublicShareLinkExpiry,
    handleCreatePublicShareLink,
    handleRevokePublicShareLink,
    handleCopyPublicShareLink,
    showSharedSessionsSidebar,
    setShowSharedSessionsSidebar,
    agentId,
  } = props;

  const normalizedExternalAccessPolicy =
    buildExternalAccessPolicy(externalAccessPolicy);
  const selectedAccessMode =
    EXTERNAL_CHAT_ACCESS_MODE_OPTIONS.find(
      (option) => option.value === normalizedExternalAccessPolicy.mode
    ) || EXTERNAL_CHAT_ACCESS_MODE_OPTIONS[0];
  const isDisabledMode =
    normalizedExternalAccessPolicy.mode === EXTERNAL_CHAT_ACCESS_MODE_DISABLED;
  const currentLinkUrl =
    publicShareLink?.url ||
    (publicShareLink?.tokenValue
      ? config.buildAgentChatUrl(publicShareLink.tokenValue)
      : "");
  const perIdentityLimitOptions = buildNumericLimitSelectOptions(
    EXTERNAL_CHAT_MAX_SESSIONS_PER_IDENTITY_PRESETS,
    externalChatMaxSessionsPerIdentity
  );
  const totalLimitOptions = buildNumericLimitSelectOptions(
    EXTERNAL_CHAT_MAX_SESSIONS_TOTAL_PRESETS,
    externalChatMaxSessionsTotal
  );
  const ratePeriodUnitLabel =
    EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS.find(
      (option) => option.value === externalChatRateLimitPeriodUnit
    )?.label || externalChatRateLimitPeriodUnit;
  const ratePeriodDisplayLabel =
    String(externalChatRateLimitPeriodValue).trim() === "1"
      ? ratePeriodUnitLabel.replace(/s$/, "")
      : ratePeriodUnitLabel;

  return (
    <>
      <AgentConfigSection id="accessLimits">
      <PublicAccessPolicyEditor
        accessPolicy={normalizedExternalAccessPolicy}
        onChange={setExternalAccessPolicy}
        users={reviewTaskEligibleUsers}
        usersLoading={loadingReviewTaskEligibleUsers}
      />
      <Typography sx={{ fontSize: "12px", color: "text.disabled", mb: 2 }}>
        {selectedAccessMode.description}
      </Typography>
      {Array.isArray(selectedAccessMode.detailLines) &&
        selectedAccessMode.detailLines.length > 0 && (
          <Box
            sx={{
              mb: 2,
              p: 1.25,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "10px",
              backgroundColor: "action.hover",
            }}
          >
            {selectedAccessMode.detailLines.map((line) => (
              <Typography
                key={line}
                sx={{
                  fontSize: "12px",
                  color: "text.secondary",
                  lineHeight: 1.6,
                }}
              >
                {`\u2022 ${line}`}
              </Typography>
            ))}
          </Box>
        )}

      <Box sx={{ mb: 2 }}>
        <ToggleRow
          checked={showSharedSessionsSidebar}
          onChange={setShowSharedSessionsSidebar}
          title="Show sessions sidebar on shared links"
          description="Turn off to hide the left session list. When hidden, the shared page shows a New chat button in the top-right header."
        />
      </Box>

      <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box>
          <Typography className="org-field-label">
            Max sessions per identity
          </Typography>
          <TextField
            className="org-styled-textfield"
            select
            fullWidth
            size="small"
            value={externalChatMaxSessionsPerIdentity}
            onChange={(event) =>
              setExternalChatMaxSessionsPerIdentity(event.target.value)
            }
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) =>
                getLimitOptionLabel(perIdentityLimitOptions, value),
            }}
          >
            {perIdentityLimitOptions.map((option) => (
              <MenuItem key={option.value || "none"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography
            sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
          >
            Caps how many sessions one identity can start from this link.
            Identity is derived from anonymous browser ID, verified email, or
            authenticated account depending on access mode.
          </Typography>
        </Box>

        <Box>
          <Typography className="org-field-label">
            Max sessions total
          </Typography>
          <TextField
            className="org-styled-textfield"
            select
            fullWidth
            size="small"
            value={externalChatMaxSessionsTotal}
            onChange={(event) =>
              setExternalChatMaxSessionsTotal(event.target.value)
            }
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) =>
                getLimitOptionLabel(totalLimitOptions, value),
            }}
          >
            {totalLimitOptions.map((option) => (
              <MenuItem key={option.value || "none"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography
            sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
          >
            Caps total sessions started across all users for this share link. To
            start this count fresh, generate a new link.
          </Typography>
        </Box>

        <Box>
          <Typography className="org-field-label">
            User message rate limit
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              className="org-styled-textfield"
              size="small"
              sx={{ width: 110 }}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              placeholder="No limit"
              value={externalChatRateLimitMessages}
              onChange={(event) =>
                setExternalChatRateLimitMessages(event.target.value)
              }
            />
            <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
              messages per
            </Typography>
            <TextField
              className="org-styled-textfield"
              size="small"
              sx={{ width: 80 }}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={externalChatRateLimitPeriodValue}
              onChange={(event) =>
                setExternalChatRateLimitPeriodValue(event.target.value)
              }
            />
            <TextField
              className="org-styled-textfield"
              select
              size="small"
              sx={{ width: 120 }}
              value={externalChatRateLimitPeriodUnit}
              onChange={(event) =>
                setExternalChatRateLimitPeriodUnit(event.target.value)
              }
            >
              {EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Typography
            sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
          >
            {`Caps how many user messages a single identity can send per ${
              externalChatRateLimitPeriodValue || "?"
            } ${ratePeriodDisplayLabel}. Counts both new sessions and follow-up messages. Leave the message count blank for no limit.`}
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <Typography className="org-field-label">
              Rate limit message
            </Typography>
            <TextField
              className="org-styled-textfield"
              fullWidth
              size="small"
              multiline
              minRows={2}
              placeholder={EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_EXCEEDED_MESSAGE}
              value={externalChatRateLimitExceededMessage}
              onChange={(event) =>
                setExternalChatRateLimitExceededMessage(event.target.value)
              }
            />
            <Typography
              sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
            >
              Shown in chat when the rate limit is hit. Leave blank to use the
              default.
            </Typography>
          </Box>
        </Box>
      </Box>
      </AgentConfigSection>

      <Divider sx={{ borderColor: "divider", my: 2.5 }} />

      <AgentConfigSection id="shareLink">
      {!agentId ? (
        <Typography sx={{ fontSize: "12px", color: "text.disabled" }}>
          Create this agent first, then generate a public chat link.
        </Typography>
      ) : (
        <>
          <Box sx={{ mb: 1.5 }}>
            <ToggleRow
              checked={enablePublicShareLinkExpiry}
              onChange={(nextChecked) => {
                setEnablePublicShareLinkExpiry(nextChecked);
                if (!nextChecked) {
                  setPublicShareLinkExpiresAt("");
                }
              }}
              title="Enable link expiry"
              description="Turn on to set an expiry date/time for newly generated links."
            />
          </Box>

          {enablePublicShareLinkExpiry && (
            <>
              <Typography className="org-field-label">
                Link expiry (optional)
              </Typography>
              <TextField
                className="org-styled-textfield"
                fullWidth
                type="datetime-local"
                size="small"
                disabled={isDisabledMode}
                value={publicShareLinkExpiresAt}
                onChange={(event) =>
                  setPublicShareLinkExpiresAt(event.target.value)
                }
                sx={{ mb: 1.5 }}
                helperText={
                  isDisabledMode
                    ? "Enable an access mode first to set link expiry."
                    : ""
                }
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          <Box sx={{ display: "flex", gap: 1, mb: 1.25 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<LinkOutlinedIcon sx={{ fontSize: 16 }} />}
              disabled={
                creatingPublicShareLink ||
                revokingPublicShareLink ||
                isDisabledMode
              }
              onClick={handleCreatePublicShareLink}
              sx={{ textTransform: "none" }}
            >
              {publicShareLink ? "Rotate link" : "Create link"}
            </Button>
            {publicShareLink?.shareId && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={revokingPublicShareLink || creatingPublicShareLink}
                onClick={handleRevokePublicShareLink}
                sx={{ textTransform: "none" }}
              >
                Revoke
              </Button>
            )}
          </Box>

          {isDisabledMode && (
            <Typography
              sx={{ fontSize: "12px", color: "text.disabled", mb: 1 }}
            >
              Set an access mode above to enable public links.
            </Typography>
          )}

          {currentLinkUrl && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                className="org-styled-textfield"
                fullWidth
                size="small"
                value={currentLinkUrl}
                InputProps={{ readOnly: true }}
              />
              <IconButton
                size="small"
                onClick={handleCopyPublicShareLink}
                title="Copy public chat link"
              >
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )}
        </>
      )}

      {publicShareLinkError && (
        <Typography sx={{ fontSize: "12px", color: "error.dark", mt: 1 }}>
          {publicShareLinkError}
        </Typography>
      )}
      </AgentConfigSection>
    </>
  );
}
